import { Injectable } from '@angular/core';
import { CurrentUserStore } from '@app/user/store/current-user/current-user.store';
import { BaseService } from '../base.service';
import { filter } from 'rxjs/operators';
import environment from '@environments/environment';
import { ResourceType } from '@app/core/model/resource-type';
import { HttpUtilityService } from '../http-utility/http-utility.service';
import { StringUtilities } from '@app/core/utilities/string-utilities';

// Service responsible to return the correct 'old website' url, taking into account the domain of the user.
@Injectable({
  providedIn: 'root'
})
export class LegacyBsApiEndpointsService extends BaseService {

  private currentUserDomain: string;
  private isPublicDomainuser: boolean;

  private websiteUrl = `${environment.siteUrl}`;

  constructor(private currentUserStore: CurrentUserStore, private httpUtilityService: HttpUtilityService) {
    super();
    this.subs.sink = this.currentUserStore.stateChanged.pipe(filter(state => state != null && state.currentUser != null)).subscribe({
      next: state => {
        this.currentUserDomain = state.currentUser.DomainName;
        this.isPublicDomainuser = state.currentUser.IsPublicDomainUser;
      }
    });
  }

  getInvitationsUrl(resourceType: ResourceType, resourceId: string): string {
    return `${this.getWebsiteUrlByDomain()}/api/collaborator/${resourceType.toString()}/${resourceId}`;
  }

  getShareByLinkUrl(resourceType: ResourceType, resourceId: string): string {
    return `${this.getWebsiteUrlByDomain()}/api/share_by_link/${resourceType.toString()}/${resourceId}`;
  }

  getRequeueSampleSheetUrl(runId: string): string {
    return `${this.getWebsiteUrlByDomain()}/run/${runId}/requeue`;
  }

  getTriggerDownloadUrl(): string {
    return `${this.getWebsiteUrlByDomain()}/apps/triggerDownload`;
  }

  getSamplesheetRequestUrl(runId: string): string {
    return `${StringUtilities.trimTrailingSlash(this.getWebsiteUrlByDomain())}/api/run/${runId}/samplesheet`;
  }
  getWebsiteApiBaseUrl(): string {
    return `${StringUtilities.trimTrailingSlash(this.getWebsiteUrlByDomain())}/api`;
  }

  getCopySamplesToProjectUrl(projectId: string): string {
    return `${this.getWebsiteApiBaseUrl()}/project/${projectId}/sample/copy`;
  }

  getMergeSamplesUrl(projectId: string): string {
    return `${this.getWebsiteApiBaseUrl()}/project/${projectId}/sample/merge`;
  }

  getWebsiteUrlByDomain(skipBrowserDomainCheck = false): string {
    // Deployed environments serve the UI from the same host that serves these legacy endpoints, so
    // the current origin is the correct base and is same-origin by construction. Deriving the host
    // from currentUserDomain instead is unreliable: it is populated asynchronously from
    // /v2/users/current, so on an initial page load the url can be built before it arrives.
    if (!this.httpUtilityService.isLocalEnvironment()) {
      return this.httpUtilityService.getBaseUrlFromCurrentRequestUrl();
    }

    // Local dev is the exception - ng serve hosts the UI on local-basespace.illumina.com while
    // siteUrl points at a separate deployed site, so the origin is not usable and the enterprise
    // domain still has to be derived. The browser domain never matches there, which is why
    // skipBrowserDomainCheck exists.
    const browserDomainMatches = this.httpUtilityService.getDomainFromCurrentRequestUrl() === this.currentUserDomain;
    if (!this.isPublicDomainuser && this.currentUserDomain && (skipBrowserDomainCheck || browserDomainMatches)) {
      return this.websiteUrl.replace('https://', `https://${this.currentUserDomain}.`);
    }
    return this.websiteUrl;
  }

  getSampleAnalysesUrl(sampleId: string){
    return `${StringUtilities.trimTrailingSlash(this.getWebsiteUrlByDomain())}/api/sample/${sampleId}/appsessions`;
  }

 getSampleFileUrl(sampleId: string){
    return `${StringUtilities.trimTrailingSlash(this.getWebsiteUrlByDomain())}/api/sample/${sampleId}/file`;
  }

}
